import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Brand from "./Brand";
import { Wind, BookOpen, Sparkles, ChevronRight, X, ShieldCheck, Heart, CalendarCheck2, Compass } from "lucide-react";

const STORAGE_KEY = "maaya_welcome_seen_v1";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_maaya-reset/artifacts/t6ziwuh2_MAYAVERSE%20-%20LOGOS%20%286%29.png";

export function hasSeenWelcome() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { /* localStorage blocked */ return true; }
}
export function markWelcomeSeen() {
  try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* localStorage blocked */ }
}
export function resetWelcomeFlag() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* localStorage blocked */ }
}

const LogoBrand = ({ small = false }) => (
  <div className="flex flex-col items-center" data-testid="logo-brand">
    <img
      src={LOGO_URL}
      alt="Daily Reset by Maayaveda"
      className={small ? "w-16 h-16 object-contain mb-4" : "w-24 h-24 object-contain mb-5"}
      data-testid="welcome-logo"
    />
    <Brand variant={small ? "lg" : "xl"} />
  </div>
);

const SCREENS = [
  {
    key: "logo",
    body: (
      <>
        <LogoBrand />
        <p className="mt-8 text-base sm:text-lg text-[#1A2E24] leading-relaxed max-w-xs mx-auto text-center">
          Less overwhelm. More clarity. <span className="text-[#D6336C] font-semibold">Three minutes at a time.</span>
        </p>
      </>
    ),
  },
  {
    key: "what",
    body: (
      <>
        <LogoBrand small />
        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.28em] text-[#234B38] text-center">What is Daily Reset?</p>
        <h2 className="text-3xl sm:text-4xl font-medium text-[#1A2E24] text-center mt-3 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          A 3-minute daily practice
        </h2>
        <p className="text-base text-[#1A2E24] text-center mt-4 max-w-sm mx-auto leading-relaxed">
          Combining <span className="font-semibold text-[#234B38]">breathwork</span>,{" "}
          <span className="font-semibold text-[#234B38]">reflection</span>, and{" "}
          <span className="font-semibold text-[#234B38]">mindful action</span> — designed to fit into the busiest day.
        </p>
        <div className="mt-7 grid grid-cols-3 gap-3 max-w-sm mx-auto">
          {[
            { icon: Wind, label: "Breathwork" },
            { icon: BookOpen, label: "Reflection" },
            { icon: Sparkles, label: "Action" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center text-center rounded-2xl bg-white border border-[#E2EBE6] py-4 px-2">
              <div className="rounded-full bg-[#234B38]/10 p-2.5 mb-2">
                <Icon className="w-4 h-4 text-[#234B38]" />
              </div>
              <p className="text-xs font-semibold text-[#1A2E24]">{label}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    key: "benefits",
    body: (
      <>
        <LogoBrand small />
        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.28em] text-[#234B38] text-center">Why it works</p>
        <h2 className="text-3xl sm:text-4xl font-medium text-[#1A2E24] text-center mt-3 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Small. Daily. Lasting.
        </h2>
        <div className="mt-7 space-y-3 max-w-sm mx-auto">
          {[
            { icon: Compass, title: "Reduce overwhelm", body: "Down-regulate your nervous system in under two minutes." },
            { icon: Heart, title: "Improve clarity", body: "Two short prompts reveal what actually needs your attention today." },
            { icon: CalendarCheck2, title: "Build consistency", body: "Tiny, doable rituals beat sporadic intensity — every time." },
            { icon: ShieldCheck, title: "Create calm", body: "A pocket of stillness you can return to whenever life gets loud." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex gap-3 items-start rounded-2xl bg-white border border-[#E2EBE6] p-4">
              <div className="rounded-full bg-[#234B38]/10 p-2 shrink-0 mt-0.5">
                <Icon className="w-4 h-4 text-[#234B38]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A2E24]">{title}</p>
                <p className="text-xs text-[#5C7568] leading-relaxed mt-0.5">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    key: "trial",
    body: (
      <>
        <LogoBrand small />
        <h2 className="text-3xl sm:text-4xl font-medium text-[#1A2E24] text-center mt-8 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="welcome-trial-heading">
          Start Your 7-Day Free Trial
        </h2>
        <p className="text-sm sm:text-base text-[#5C7568] text-center mt-3 max-w-xs mx-auto leading-relaxed" data-testid="welcome-trial-subtext">
          7 days free. No card required. Cancel anytime.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3 max-w-sm mx-auto">
          <div className="rounded-2xl bg-white border border-[#E2EBE6] p-4 text-center" data-testid="plan-card-monthly">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C7568]">After trial · Monthly</p>
            <p className="text-2xl font-medium text-[#234B38] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>₹149</p>
            <p className="text-[10px] text-[#5C7568] mt-0.5">per month</p>
          </div>
          <div className="rounded-2xl bg-[#234B38]/5 border-2 border-[#234B38] p-4 text-center relative" data-testid="plan-card-yearly">
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#D6336C] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Save ₹789</span>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C7568]">After trial · Yearly</p>
            <p className="text-2xl font-medium text-[#234B38] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>₹999</p>
            <p className="text-[10px] text-[#5C7568] mt-0.5">per year</p>
          </div>
        </div>
        <p className="text-[11px] text-[#5C7568] text-center mt-5 max-w-xs mx-auto leading-relaxed" data-testid="welcome-trial-disclaimer">
          After the 7-day trial, Pro continues at the plan you choose. Daily Reset is not a permanently free service — billing begins after trial unless you cancel.
        </p>
      </>
    ),
  },
  {
    key: "cta",
    body: (
      <>
        <LogoBrand small />
        <h2 className="text-3xl sm:text-4xl font-medium text-[#1A2E24] text-center mt-8 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Ready in 30 seconds
        </h2>
        <p className="text-sm text-[#5C7568] text-center mt-3 max-w-xs mx-auto leading-relaxed">
          Create an account or sign in to continue. Your free trial starts the moment you arrive.
        </p>
      </>
    ),
  },
];

export default function WelcomeOnboarding({ onClose }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const total = SCREENS.length;
  const last = step === total - 1;
  const screen = SCREENS[step];

  const finish = () => { markWelcomeSeen(); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[200] bg-[#FDFBF7] overflow-y-auto" data-testid="welcome-onboarding">
      <button
        onClick={finish}
        data-testid="welcome-skip-btn"
        className="absolute top-4 right-4 z-10 text-xs text-[#5C7568] hover:text-[#1A2E24] flex items-center gap-1 rounded-full bg-white/80 backdrop-blur px-3 py-1.5 border border-[#E2EBE6]"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      >
        Skip <X className="w-3 h-3" />
      </button>

      <div
        className="min-h-screen flex flex-col px-6 pt-20 pb-8"
        data-testid={`welcome-screen-${screen.key}`}
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 5rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
        }}
      >
        <div className="flex-1 flex flex-col justify-center">{screen.body}</div>

        <div className="flex justify-center gap-2 mt-8" data-testid="welcome-dots">
          {SCREENS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`welcome-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#234B38]" : "w-1.5 bg-[#E2EBE6]"}`}
              aria-label={`Go to screen ${i + 1}`}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-2 max-w-sm mx-auto w-full">
          {last ? (
            <>
              <button
                onClick={() => { finish(); navigate("/signup"); }}
                data-testid="welcome-signup-btn"
                className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3.5 font-medium transition-colors"
              >
                Create account · Start trial
              </button>
              <button
                onClick={() => { finish(); navigate("/login"); }}
                data-testid="welcome-login-btn"
                className="w-full rounded-full bg-white border border-[#E2EBE6] text-[#1A2E24] py-3.5 font-medium hover:bg-white/60 transition-colors"
              >
                I already have an account
              </button>
            </>
          ) : (
            <button
              onClick={() => setStep((s) => Math.min(s + 1, total - 1))}
              data-testid="welcome-next-btn"
              className="w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-3.5 font-medium transition-colors flex items-center justify-center gap-2"
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
